<template>
  <b-container fluid>
    <div class="students">
      <h1>Students</h1>
      <div class="table-wrap">
        <div>
          <router-link
            v-if="students.length > 0 && isAdmin"
            :to="{ name: 'NewStudent' }"
            class
            >+Add Student</router-link
          >
        </div>
        <br />
        <b-row>
          <b-col>
            <name-search
              table="students-list"
              parent="students-list"
            ></name-search>
            <b-button @click="resetList" class="mb-2">Reset Search</b-button>
          </b-col>
          <b-col md="6" class="my-1">
            <b-form-group horizontal label="Per page">
              <b-form-select :options="pageOptions" v-model="perPage" />
            </b-form-group>
          </b-col>
        </b-row>
        <div v-if="students.length > 0" class="table-wrap">
          <br />
          <b-row class="mt-4">
            <b-col>
              <b-form-group horizontal label="Filter">
                <b-input-group>
                  <b-form-input v-model="filter" placeholder="Type to Search" />
                  <b-input-group-append>
                    <b-btn :disabled="!filter" @click="filter = ''"
                      >Clear</b-btn
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="6" class="my-1">
              <b-form-group horizontal label="Per page">
                <b-form-select :options="pageOptions" v-model="perPage" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-table
              v-cloak
              responsive
              :fields="fields"
              :items="students"
              :striped="true"
              :bordered="true"
              :hover="true"
              stacked="md"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filter"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              @filtered="onFiltered"
            >
              <template slot="cell(student_name)" slot-scope="row">
                <b-link
                  @click.stop="stopClick"
                  :to="{
                    name: 'EditStudent',
                    params: { id: row.item.student_uuid },
                  }"
                  >{{ row.value }}</b-link
                >
              </template>
              <template slot="cell(school_name)" slot-scope="row">
                <b-link
                  v-if="isAdmin"
                  :to="{
                    name: 'EditSchool',
                    params: { id: row.item.school_uuid },
                  }"
                  >{{ row.value }}</b-link
                >
                <p v-if="!isAdmin">{{ row.value }}</p>
              </template>
              <template slot="cell(teacher_name)" slot-scope="row">
                <b-link
                  v-if="isAdmin"
                  :to="{
                    name: 'EditTeacher',
                    params: { id: row.item.teacher_uuid },
                  }"
                  >{{ row.value }}</b-link
                >
                <p v-if="!isAdmin">{{ row.value }}</p>
              </template>
              <template slot="cell(id)" slot-scope="row">
                <b-link
                  v-if="isAdmin"
                  @click.stop="stopClick"
                  :to="{ name: 'EditStudent', params: { id: row.item.id } }"
                  >Edit</b-link
                >
              </template>
              <template slot="cell(active)" slot-scope="row">
                <b-form-radio-group
                  :id="row.index.toString()"
                  :value="row.item.active"
                  v-model="row.item.active"
                  @change="updateStudentActive(row.item)"
                >
                  <b-form-radio :value="true">Yes</b-form-radio>
                  <b-form-radio :value="false">No</b-form-radio>
                </b-form-radio-group>
              </template>
              <template slot="cell(duplicate)" slot-scope="row">
                <b-form-checkbox
                  v-model="row.item.duplicate"
                  name="duplicate"
                  @change="updateStudentDuplicate(row.item)"
                  >Duplicate</b-form-checkbox
                >
              </template>
            </b-table>
          </b-row>
          <b-row>
            <b-col md="6" class="my-1">
              <b-pagination
                :total-rows="totalRows"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0"
              />
            </b-col>
            <download-button :export="students" :file="file"></download-button>
          </b-row>
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import DownloadButton from "@/components/DownloadButton";
import NameSearch from "@/components/NameSearch";
import StudentService from "@/services/StudentService";

export default {
  name: "StudentsList",
  components: {
    DownloadButton,
    NameSearch,
  },
  data() {
    return {
      fields: [
        { key: "student_name", label: "Full Name" },
        { key: "sex", label: "Sex", sortable: true },
        { key: "school_name", label: "Current School", sortable: true },
        { key: "teacher_name", label: "Current Teacher", sortable: true },
        { key: "district", label: "District", sortable: true },
        { key: "village", label: "Village", sortable: true },
        { key: "active", label: "Active", sortable: true },
        { key: "duplicate", label: "Duplicate", sortable: true },
      ],
      file: this.$route.path.slice(1),
      currentPage: 1,
      country: "",
      perPage: 20,
      pageOptions: [20, 50, 100],
      sortBy: null,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
    };
  },
  methods: {
    ...mapActions({
      SEARCH_STUDENTS_LIST: "students/SEARCH_STUDENTS_LIST",
      RESET_STUDENTS_STATE: "students/RESET_STUDENTS_STATE",
      RESET_SEARCH_STATE: "searches/RESET_SEARCH_STATE",
    }),
    async updateStudentActive(params) {
      const payload = await {
        uuid: params.student_uuid,
        active: !(params.active !== "false"),
      };
      await StudentService.updateStudentActive(payload);
    },
    async updateStudentDuplicate(params) {
      const payload = await {
        uuid: params.student_uuid,
        user_flagged_duplicate: !params.duplicate,
      };
      await StudentService.updateStudentDuplicate(payload);
    },
    async resetList() {
      await this.RESET_SEARCH_STATE();
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    stopClick(row) {},
  },
  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
    }),
    students: {
      get() {
        return this.$store.state.students.students;
      },
    },
    totalRows() {
      return this.students.length || 0;
    },
  },
};
</script>

<style scoped>
#students {
  table-layout: fixed;
  width: 100%;
  white-space: nowrap;
}
#name {
  width: 30%;
}
#sex {
  width: 10%;
}
#assigned-schools {
  width: 60%;
}
#edit {
  width: 10%;
}
.table-wrap {
  width: 80%;
  margin: 0 auto;
  text-align: center;
}
table th,
table tr {
  text-align: center;
}
.test-red {
  background: #e72828;
}
table thead {
  background: #bf9660;
}
td {
  width: 100px;
  text-align: center;
}
table tr td {
  padding: 10px;
}
table tr:nth-child(odd) {
  background: #f2f2f2;
}
table tr:nth-child(1) {
  background: #bf9660;
  color: #fff;
}
a {
  color: #4d7ef7;
  text-decoration: none;
}
a.add_student_link {
  background: #4d7ef7;
  color: #fff;
  padding: 10px 80px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
}
</style>
